import { MSAData, SecondaryStructure } from '../../types';

export interface TrackConfig {
  height: number;
  labelWidth: number;
  sequenceLength: number;
  displayStart: number;
  displayEnd: number;
}

export interface TrackData {
  msaData: MSAData;
  notation?: SecondaryStructure;
}

export abstract class BaseTrack {
  protected config: TrackConfig;
  protected data: TrackData;
  protected element?: HTMLElement;
  protected trackId: string;

  constructor(trackId: string, config: TrackConfig, data: TrackData) {
    this.trackId = trackId;
    this.config = config;
    this.data = data;
  }

  /**
   * Create the HTML structure for this track
   */
  abstract createHTML(): string;

  /**
   * Setup the track after HTML is rendered
   */
  abstract setup(container: HTMLElement): void;

  /**
   * Update the track when data or config changes
   */
  abstract update(config?: Partial<TrackConfig>, data?: Partial<TrackData>): void;

  /**
   * Clean up the track
   */
  abstract destroy(): void;

  /**
   * Get the track element
   */
  getElement(): HTMLElement | undefined {
    return this.element;
  }

  /**
   * Update configuration
   */
  updateConfig(newConfig: Partial<TrackConfig>): void {
    this.config = { ...this.config, ...newConfig };
    this.update();
  }

  /**
   * Update data
   */
  updateData(newData: Partial<TrackData>): void {
    this.data = { ...this.data, ...newData };
    this.update();
  }

  /**
   * Check if track has required data
   */
  protected hasRequiredData(): boolean {
    return !!this.data.msaData;
  }

  /**
   * Get common nightingale attributes
   */
  protected getNightingaleAttributes(): Record<string, string> {
    return {
      'length': this.config.sequenceLength.toString(),
      'display-start': this.config.displayStart.toString(),
      'display-end': this.config.displayEnd.toString(),
      'height': this.config.height.toString()
    };
  }

  /**
   * Apply attributes to an element
   */
  protected applyAttributes(element: HTMLElement, attributes: Record<string, string>): void {
    Object.entries(attributes).forEach(([key, value]) => {
      element.setAttribute(key, value);
    });
  }

  /**
   * Create label HTML
   */
  protected createLabel(text: string): string {
    return `
      <div style="width: ${this.config.labelWidth}px">
        ${text}
      </div>
    `;
  }
}